import { Component, Input, OnChanges, SimpleChanges, OnDestroy, OnInit, ViewChild, ElementRef } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ParserOutput } from '../../workers/pos-parser.worker';

type ViewMode = 'Gnd Trk' | 'Position' | 'Velocity' | 'Accel' | 'NSat';

@Component({
  selector: 'app-ppk-plot',
  templateUrl: './ppk-plot.component.html',
  styleUrls: ['./ppk-plot.component.scss']
})
export class PpkPlotComponent implements OnInit, OnChanges, OnDestroy {
  @Input() fileUrl: string | undefined;
  @ViewChild('plot', { static: false }) plotEl!: ElementRef<HTMLDivElement>;

  viewModes: ViewMode[] = ['Gnd Trk', 'Position', 'Velocity', 'Accel', 'NSat'];
  currentMode: ViewMode = 'Gnd Trk';
  isMapMode = false;
  backgroundMode: 'Grid' | 'Map' = 'Grid';
  isLoading = false;
  errorMsg = '';
  selectedQ: 'ALL' | 1 | 2 | 3 | 4 | 5 = 'ALL';
  scaleBarCm = 1;
  scaleOptions = [
    { label: '1 mm', cm: 0.1 },
    { label: '2 mm', cm: 0.2 },
    { label: '5 mm', cm: 0.5 },
    { label: '1 cm', cm: 1 },
    { label: '2 cm', cm: 2 },
    { label: '5 cm', cm: 5 },
    { label: '10 cm', cm: 10 },
    { label: '1 m', cm: 100 },
    { label: '1 km', cm: 100000 },
    { label: '100 km', cm: 10000000 }
  ];

  data: ParserOutput | null = null;

  statusText = '';
  fixRateText = '';
  timeRangeText = '';
  cursorInfo = '';

  private worker: Worker | null = null;
  private Plotly: any = null;

  constructor(private http: HttpClient) {}

  ngOnInit(): void {
    if (typeof Worker !== 'undefined') {
      this.worker = new Worker(new URL('../../workers/pos-parser.worker', import.meta.url));
      this.worker.onmessage = ({ data }) => {
        this.data = data as ParserOutput;
        this.isLoading = false;
        this.updateStatus();
        this.updatePlot();
      };
    } else {
      this.errorMsg = 'Web Workers are not supported.';
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['fileUrl'] && this.fileUrl) {
      this.loadFile(this.fileUrl);
    }
  }

  ngOnDestroy(): void {
    this.worker?.terminate();
  }

  loadFile(url: string): void {
    this.isLoading = true;
    this.errorMsg = '';
    this.http.get(url, { responseType: 'text' }).subscribe({
      next: (content) => this.worker?.postMessage({ rawData: content }),
      error: (err) => { this.isLoading = false; this.errorMsg = 'Failed to download result file.'; console.error(err); }
    });
  }

  setMode(mode: ViewMode): void {
    this.currentMode = mode;
    this.updatePlot();
  }

  onBackgroundChange(): void {
    this.isMapMode = this.backgroundMode === 'Map';
    this.updatePlot();
  }

  updateStatus(): void {
    if (!this.data) return;
    const s = this.data.stats;
    const total = s.total || 1;
    const q1Pct = ((s.q1 / total) * 100).toFixed(1);
    this.fixRateText = `Fix: ${q1Pct}%`;
    this.statusText = `Data Points: Q=1: ${s.q1}  Q=2: ${s.q2}  Q=5: ${s.q5}  Total: ${total}`;
    
    // Time range
    if (this.data.times && this.data.times.length > 0) {
      const start = this.data.times[0];
      const end = this.data.times[this.data.times.length - 1];
      this.timeRangeText = `Data Collection Duration: ${start} - ${end} GPST`;
    } else {
      this.timeRangeText = '';
    }
  }

  async updatePlot(): Promise<void> {
    if (!this.data) return;
    const d = this.data;

    const commonLayout = {
      font: { family: 'Arial, sans-serif', size: 12 },
      showlegend: false,
      legend: { orientation: 'h', y: 1.1 },
      margin: { l: 20, r: 20, t: 10, b: 10 },
      xaxis: { showgrid: true, gridcolor: '#f2f2f2', zeroline: false },
      yaxis: { showgrid: true, gridcolor: '#f2f2f2', zeroline: false }
    };

    const idx = d.q.map((q, i) => ({ q, i })).filter(item => this.selectedQ === 'ALL' || item.q === this.selectedQ).map(item => item.i);
    const pick = <T>(arr: T[]) => idx.map(i => arr[i]);

    const colors = pick(d.q).map(q => {
      if (q === 1) return '#008000';
      if (q === 2) return '#FFA500';
      if (q === 3) return '#FFD700';
      if (q === 4) return '#0000FF';
      if (q === 5) return '#FF0000';
      if (q === 6) return '#800080';
      return '#888888';
    });

    const filtered: ParserOutput = {
      times: pick(d.times),
      east: pick(d.east),
      north: pick(d.north),
      up: pick(d.up),
      lat: d.lat ? pick(d.lat) : [],
      lon: d.lon ? pick(d.lon) : [],
      hgt: d.hgt ? pick(d.hgt) : [],
      velE: pick(d.velE),
      velN: pick(d.velN),
      velU: pick(d.velU),
      accE: pick(d.accE),
      accN: pick(d.accN),
      accU: pick(d.accU),
      ns: pick(d.ns),
      age: pick(d.age),
      ratio: pick(d.ratio),
      q: pick(d.q),
      stats: d.stats,
      basePos: d.basePos
    };

    const Plotly = await this.ensurePlotly().catch(err => {
      this.errorMsg = 'Failed to load plotting library. Please check your connection.';
      console.error('Plotly load failed:', err);
      return null;
    });
    if (!Plotly || !this.plotEl) return;

    if (idx.length === 0) {
      const layoutEmpty = {
        ...commonLayout,
        title: '',
        xaxis: { showticklabels: false, showgrid: false, zeroline: false, visible: false },
        yaxis: { showticklabels: false, showgrid: false, zeroline: false, visible: false },
        annotations: [{ text: 'No points for selected Q filter', showarrow: false, xref: 'paper', yref: 'paper', x: 0.5, y: 0.5, font: { size: 12, color: '#666' } }]
      };
      Plotly.react(this.plotEl.nativeElement, [], layoutEmpty, { displaylogo: false, responsive: true });
      return;
    }

    let traces: any[] = [];
    let layout: any = {};
    const config = { responsive: true, displaylogo: false, modeBarButtonsToRemove: ['lasso2d', 'select2d'], displayModeBar: true };

    switch (this.currentMode) {
      case 'Gnd Trk':
        [traces, layout] = this.buildGndTrk(filtered, colors, commonLayout);
        break;
      case 'Position':
        [traces, layout] = this.buildTimeSeries(d.times, [d.east, d.north, d.up], ['E-W (m)', 'N-S (m)', 'U-D (m)'], colors, commonLayout);
        break;
      case 'Velocity':
        [traces, layout] = this.buildTimeSeries(d.times, [d.velE, d.velN, d.velU], ['E-W (m/s)', 'N-S (m/s)', 'U-D (m/s)'], colors, commonLayout);
        break;
      case 'Accel':
        [traces, layout] = this.buildTimeSeries(d.times, [d.accE, d.accN, d.accU], ['E-W (m/s²)', 'N-S (m/s²)', 'U-D (m/s²)'], colors, commonLayout);
        break;
      case 'NSat':
        [traces, layout] = this.buildNSat(d, commonLayout);
        break;
    }

    Plotly.react(this.plotEl.nativeElement, traces, layout, config).then(() => {
      // Re-attach listener on every react to ensure it's there
      const el = this.plotEl.nativeElement as any;
      if (el.removeAllListeners) {
        el.removeAllListeners('plotly_relayout');
        el.removeAllListeners('plotly_click');
      }
      if (el.on) {
        el.on('plotly_relayout', (event: any) => this.onRelayout(event));
        el.on('plotly_click', (event: any) => this.onPointClick(event));
      }

      // Force update scale bar position immediately after plot to match actual aspect-ratio-adjusted ranges
      if (this.currentMode === 'Gnd Trk' && !this.isMapMode) {
        this.updateScaleBarPosition();
      }
    });
  }

  private isRelayouting = false;
  
  // Wrapper for event listener
  private onRelayout(event: any): void {
    if (this.isRelayouting) return;
    // Only react to range changes
    if (!event || (!event['xaxis.range[0]'] && !event['xaxis.range'] && !event['autosize'])) return;
    
    this.updateScaleBarPosition();
  }

  private onPointDoubleClick(event: any): void {
    // We cannot easily get point data from a raw doubleclick event on the background
    // But plotly_doubleclick usually resets zoom. 
    // Wait, the user wants "Double-click point". 
    // Plotly doesn't have a native 'plotly_doubleclick' event that passes point data like 'plotly_click' does.
    // 'plotly_doubleclick' is usually just a signal to reset zoom.
    
    // Workaround: We can use 'plotly_click' and check for double click timing manually, 
    // OR, we can try to rely on browser dblclick if Plotly exposes it well enough.
    // However, Plotly intercepts dblclick for zoom reset by default.
    // Let's implement a manual double-click detection on 'plotly_click'.
  }
  
  private lastClickTime = 0;
  private onPointClick(event: any): void {
    const now = Date.now();
    if (now - this.lastClickTime < 300) {
      // Double click detected
      this.handlePointAction(event);
    }
    this.lastClickTime = now;
  }

  private handlePointAction(event: any): void {
    if (!event || !event.points || event.points.length === 0) return;
    const p = event.points[0];
    let lat: number | undefined;
    let lon: number | undefined;

    // Grid View: customdata = [lat, lon, east, north]
    if (this.currentMode === 'Gnd Trk' && !this.isMapMode && p.customdata && p.customdata.length >= 2) {
      lat = p.customdata[0];
      lon = p.customdata[1];
    } 
    // Map View: p.lat / p.lon
    else if (p.lat !== undefined && p.lon !== undefined) {
      lat = p.lat;
      lon = p.lon;
    }

    if (lat !== undefined && lon !== undefined) {
      // Open Google Maps Satellite view (t=k)
      const url = `https://www.google.com/maps?q=${lat},${lon}&t=k`;
      window.open(url, '_blank');
    }
  }

  private updateScaleBarPosition(): void {
    if (this.isRelayouting) return;
    // We only care if we are in Gnd Trk mode and not in Map mode (where we manage scale bar manually)
    if (this.currentMode !== 'Gnd Trk' || this.isMapMode) return;

    const el = this.plotEl.nativeElement as any;
    // Check if layout exists
    if (!el || !el.layout) return;
    const layout = el.layout;
    const xRange = layout.xaxis.range;
    const yRange = layout.yaxis.range;

    if (!xRange || !yRange) return;

    const spanX = xRange[1] - xRange[0];
    const spanY = yRange[1] - yRange[0];

    // Keep using data coordinates to ensure length = 1 unit (matching grid)
    // margin: 5% of X span
    const marginX = spanX * 0.05;
    const marginY = spanX * 0.05; // Use X span for margin to keep it proportional to width

    const xEnd = xRange[1] - marginX;
    const xStart = xEnd - 1; // Length is always 1 data unit
    const yPos = yRange[0] + marginY; // Bottom + margin

    this.isRelayouting = true;
    this.Plotly.relayout(this.plotEl.nativeElement, {
      'shapes[0].x0': xStart,
      'shapes[0].x1': xEnd,
      'shapes[0].y0': yPos,
      'shapes[0].y1': yPos,
      'annotations[0].x': (xStart + xEnd) / 2,
      'annotations[0].y': yPos - spanX * 0.03, // Text slightly below/above
      // Ensure we stay in data coordinates
      'shapes[0].xref': 'x',
      'shapes[0].yref': 'y',
      'annotations[0].xref': 'x',
      'annotations[0].yref': 'y'
    }).then(() => {
      this.isRelayouting = false;
    });
  }

  private async ensurePlotly(): Promise<any> {
    if (this.Plotly) return this.Plotly;
    const mod = await import('plotly.js-dist-min');
    // Handle both CommonJS and ES module exports
    this.Plotly = mod.default || mod;
    return this.Plotly;
  }

  private formatScaleLabel(): string {
    const opt = this.scaleOptions.find(o => o.cm === this.scaleBarCm);
    if (opt) return opt.label;
    // fallback
    if (this.scaleBarCm >= 10000000) return `${(this.scaleBarCm / 10000000).toFixed(0)}00 km`;
    if (this.scaleBarCm >= 100000) return `${(this.scaleBarCm / 100000).toFixed(0)} km`;
    if (this.scaleBarCm >= 100) return `${(this.scaleBarCm / 100).toFixed(0)} m`;
    if (this.scaleBarCm >= 1) return `${this.scaleBarCm.toFixed(0)} cm`;
    if (this.scaleBarCm >= 0.1) return `${(this.scaleBarCm * 10).toFixed(0)} mm`;
    return `${this.scaleBarCm} cm`;
  }

  private buildGndTrk(d: ParserOutput, colors: string[], baseLayout: any): [any[], any] {
    if (this.isMapMode) {
      // Map hover template to show actual lat/lon
      const traces = [{
        type: 'scattermapbox',
        lat: d.lat, lon: d.lon,
        mode: 'markers',
        marker: { size: 10, color: colors },
        text: d.times,
        customdata: d.times.map((t, i) => [d.east[i], d.north[i]]),
        hovertemplate: 'Time: %{text}<br>Lat: %{lat:.8f}<br>Lon: %{lon:.8f}<br>East: %{customdata[0]:.2f} m<br>North: %{customdata[1]:.2f} m<br><i>Double-click point to open Google Maps (Satellite)</i><extra></extra>',
        name: 'Rover'
      } as any];
      const layout = {
        ...baseLayout,
        mapbox: { style: 'open-street-map', center: { lat: d.lat[0], lon: d.lon[0] }, zoom: 15 },
        margin: { l: 0, r: 0, t: 0, b: 0 },
        showlegend: false,
        title: ''
      };
      return [traces, layout];
    } else {
      const toCm = (arr: number[]) => arr.map(v => v * 100);
      const eastCm = toCm(d.east);
      const northCm = toCm(d.north);

      // center data
      const mean = (arr: number[]) => arr.reduce((s, v) => s + v, 0) / (arr.length || 1);
      const eastCenter = mean(eastCm);
      const northCenter = mean(northCm);
      const east = eastCm.map(v => (v - eastCenter) / this.scaleBarCm);   // scaled units
      const north = northCm.map(v => (v - northCenter) / this.scaleBarCm);

      // Lock grid view to fixed range (e.g. +/- 6 grids) to keep grid visual size constant
      // Data points will zoom in/out relative to this fixed grid.
      const span = 6; 
      const xRange = [-span, span];
      const yRange = [-span, span];

      // Use 'scatter' instead of 'scattergl' for better compatibility in production
      // unless we have a huge number of points (> 10000)
      const useWebGL = d.times.length > 10000;
      const type = useWebGL ? 'scattergl' : 'scatter';

      const traces = [{
        type: type,
        x: east, y: north,
        mode: 'markers',
        marker: { size: 4, color: colors },
        text: d.times,
        customdata: d.times.map((t, i) => [d.lat[i], d.lon[i], eastCm[i] - eastCenter, northCm[i] - northCenter]),
        hovertemplate: 'Time: %{text}<br>Lat: %{customdata[0]:.8f}<br>Lon: %{customdata[1]:.8f}<br>East: %{customdata[2]:.1f} cm<br>North: %{customdata[3]:.1f} cm<br><i>Double-click point to open Google Maps (Satellite)</i><extra></extra>',
        name: 'Rover'
      }];
      const layout = {
        ...baseLayout,
        title: '',
        xaxis: { ...baseLayout.xaxis, title: '', showticklabels: false, showline: false, zeroline: false, scaleanchor: 'y', scaleratio: 1, range: xRange, dtick: 1 },
        yaxis: { ...baseLayout.yaxis, title: '', showticklabels: false, showline: false, zeroline: false, range: yRange, dtick: 1 },
        showlegend: false
      };

      // Use 'x'/'y' (data) coordinates to ensure length matches grid exactly (1 unit)
      // We will update the position in onRelayout to keep it in the corner
      const initialSpan = xRange[1] - xRange[0]; // 12
      const margin = initialSpan * 0.05; // 0.6
      const xEnd = xRange[1] - margin; // 5.4
      const xStart = xEnd - 1; // 4.4 (Length = 1)
      const yPos = yRange[0] + margin; // -5.4

      layout.shapes = [
        {
          type: 'line',
          xref: 'x', yref: 'y',
          x0: xStart, x1: xEnd,
          y0: yPos, y1: yPos,
          line: { color: '#333', width: 2 }
        }
      ];
      layout.annotations = [
        {
          xref: 'x', yref: 'y',
          x: (xStart + xEnd) / 2,
          y: yPos - initialSpan * 0.03,
          text: this.formatScaleLabel(),
          showarrow: false,
          font: { size: 10, color: '#333' },
          xanchor: 'center',
          yanchor: 'top'
        }
      ];
      return [traces, layout];
    }
  }

  private buildTimeSeries(times: string[], datas: number[][], titles: string[], colors: string[], baseLayout: any): [any[], any] {
    const useWebGL = times.length > 10000;
    const type = useWebGL ? 'scattergl' : 'scatter';
    
    const traces = datas.map((data, i) => ({
      x: times,
      y: data,
      xaxis: 'x',
      yaxis: `y${i + 1}`,
      type: type,
      mode: 'markers',
      marker: { size: 3, color: colors },
      showlegend: false
    }));
    const layout = {
      ...baseLayout,
      grid: { rows: 3, columns: 1, pattern: 'independent', roworder: 'top to bottom' },
      xaxis: { ...baseLayout.xaxis, anchor: 'y3' },
      yaxis: { domain: [0.70, 1], title: titles[0], zeroline: true, zerolinecolor: '#999' },
      yaxis2: { domain: [0.36, 0.64], title: titles[1], zeroline: true, zerolinecolor: '#999' },
      yaxis3: { domain: [0, 0.28], title: titles[2], zeroline: true, zerolinecolor: '#999' }
    };
    return [traces, layout];
  }

  private buildNSat(d: ParserOutput, baseLayout: any): [any[], any] {
    const color = '#008000';
    const useWebGL = d.times.length > 10000;
    const type = useWebGL ? 'scattergl' : 'scatter';

    const traces = [
      { x: d.times, y: d.ns, type: type, mode: 'markers', marker: { size: 3, color }, xaxis: 'x', yaxis: 'y1', name: 'NSat' },
      { x: d.times, y: d.age, type: type, mode: 'markers', marker: { size: 3, color }, xaxis: 'x', yaxis: 'y2', name: 'Age' },
      { x: d.times, y: d.ratio, type: type, mode: 'markers', marker: { size: 3, color }, xaxis: 'x', yaxis: 'y3', name: 'Ratio' }
    ];
    const layout = {
      ...baseLayout,
      grid: { rows: 3, columns: 1, pattern: 'independent', roworder: 'top to bottom' },
      xaxis: { ...baseLayout.xaxis, anchor: 'y3' },
      yaxis: { domain: [0.70, 1], title: '# Satellites' },
      yaxis2: { domain: [0.36, 0.64], title: 'Age (s)' },
      yaxis3: { domain: [0, 0.28], title: 'Ratio' }
    };
    return [traces, layout];
  }
}

